import { useState, useEffect, useCallback } from 'react';

interface PWAUpdateState {
  updateAvailable: boolean;
  updating: boolean;
  registration: ServiceWorkerRegistration | null;
}

export function usePWAUpdate() {
  const [state, setState] = useState<PWAUpdateState>({
    updateAvailable: false,
    updating: false,
    registration: null,
  });

  useEffect(() => {
    const setupUpdateDetection = async () => {
      if (!('serviceWorker' in navigator)) return;

      try {
        const registration = await navigator.serviceWorker.ready;
        
        setState(prev => ({ ...prev, registration }));

        // Check if there's already a waiting service worker
        if (registration.waiting) {
          setState(prev => ({ ...prev, updateAvailable: true }));
        }

        // Listen for new service worker installing
        const handleUpdateFound = () => {
          const newWorker = registration.installing;
          if (newWorker) {
            const handleStateChange = () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setState(prev => ({ ...prev, updateAvailable: true }));
              }
            };
            newWorker.addEventListener('statechange', handleStateChange);
          }
        };

        registration.addEventListener('updatefound', handleUpdateFound);

        // Listen for controller change
        const handleControllerChange = () => {
          setState(prev => {
            if (!prev.updating) {
              window.location.reload();
            }
            return prev;
          });
        };

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        // Check for updates when page becomes visible
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            registration.update();
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Periodic update check
        const updateInterval = setInterval(() => {
          if (!document.hidden) {
            registration.update();
          }
        }, 5 * 60 * 1000); // Check every 5 minutes

        return () => {
          registration.removeEventListener('updatefound', handleUpdateFound);
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          clearInterval(updateInterval);
        };
      } catch (error) {
        console.error('Error setting up PWA update detection:', error);
      }
    };

    setupUpdateDetection();
  }, []);

  const performUpdate = useCallback(async () => {
    if (!state.registration || state.updating) return false;

    try {
      setState(prev => ({ ...prev, updating: true }));

      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('All caches cleared successfully');
      }

      // Clear specific localStorage items
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('ajel_') ||
          key.includes('chat_thread_') ||
          key.includes('notification-banner-dismissed') ||
          key.includes('dashboardAuth')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage
      sessionStorage.clear();

      // Tell the waiting service worker to skip waiting
      if (state.registration.waiting) {
        state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // Give some time for the service worker to take control
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Force reload to get the new version
      window.location.reload();
      
      return true;
    } catch (error) {
      console.error('Error during PWA update:', error);
      setState(prev => ({ ...prev, updating: false }));
      // Fallback: force reload anyway
      window.location.reload();
      return false;
    }
  }, [state.registration, state.updating]);

  const dismissUpdate = useCallback(() => {
    setState(prev => ({ ...prev, updateAvailable: false }));
    sessionStorage.setItem('update-dismissed', 'true');
  }, []);

  return {
    updateAvailable: state.updateAvailable && !sessionStorage.getItem('update-dismissed'),
    updating: state.updating,
    performUpdate,
    dismissUpdate,
  };
}